import StarRateIcon from "@mui/icons-material/StarRate";
import ShareButton from "../ShareButton";
import { Box, Divider, ImageList, ImageListItem } from "@mui/material";
import Image from "next/legacy/image";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import GetDiscountCard from "./GetDiscountCard";
import { DiscountType } from "src/types/discountTypes";
import HaveQuestions from "../HaveQuestions";

function srcset(image: string, size: number, rows = 1, cols = 1) {
  return {
    src: `${image}?w=${size * cols}&h=${size * rows}&fit=crop&auto=format`,
    srcSet: `${image}?w=${size * cols}&h=${
      size * rows
    }&fit=crop&auto=format&dpr=2 2x`,
  };
}

export default function LargeDiscountContent({
  discount,
  shareLinkObject,
  loading,
  localeStrings,
  buttonText,
  timeLeftString,
  handleSendCodes,
  smAndDown,
  canGetCodes,
}: {
  discount: DiscountType & { numCouponsFromDb: number };
  shareLinkObject: any;
  loading: any;
  localeStrings: any;
  buttonText: string;
  timeLeftString: string;
  handleSendCodes: any;
  smAndDown?: boolean;
  canGetCodes: boolean;
}) {
  return (
    <>
      <h1 className="text-text mb-small">{discount.shortName}</h1>
      <h2 className="text-text headline-4">{discount.subtitle1}</h2>
      <div className="flex align-items-center justify-content-space-between mt-huge">
        <div className="flex gap-large">
          <div className="flex card-icon-small fit-content relative">
            <Image
              alt={discount?.logo?.alt ?? "discount"}
              src={discount?.logo?.url ?? "/assets/project_placeholder.svg"}
              layout="fill"
              sizes="(min-width: 100vw) 100vw,
                 100vw"
              objectFit="cover"
              quality={80}
              className="rounded-all-full"
            />
          </div>
          <div className="d-grid align-items-center">
            <a
              href={discount.domain}
              target="_blank"
              rel="noreferrer"
              className="text-text pointer subtitle-4"
              style={{ textDecoration: "none" }}
              children={discount.compName}
            />
            <div className="flex align-items-center">
              {/* @ts-ignore*/}
              <StarRateIcon color="textColors" />
              <p className="text-text subtitle-4">{discount.googleRating}</p>
            </div>
          </div>
        </div>

        <div className="flex gap-large">
          <ShareButton linkObj={shareLinkObject} smAndDown={smAndDown} />
          <div className="flex gap-normal">
            <Image
              src="/assets/heart-plus.svg"
              width="20"
              height="20"
              alt="save-icon"
            />
            <h6 className="subtitle-4 underline-link">
              {localeStrings.uiGeneral.saveButton}
            </h6>
          </div>
        </div>
      </div>
      <ImageList
        className="rounded-all-normal"
        variant="quilted"
        cols={4}
        rowHeight={280}
        gap={10}
      >
        {discount.images.map((item, index) => (
          <ImageListItem
            key={index}
            cols={parseInt(item.customData.cols) || 1}
            rows={parseInt(item.customData.rows) || 1}
          >
            <Image
              {...srcset(
                item.url,
                280,
                parseInt(item.customData.rows),
                parseInt(item.customData.cols)
              )}
              priority={false}
              alt={item.alt}
              loading="lazy"
              sizes="(min-width: 100vw) 100vw,
                 100vw"
              layout="fill"
              quality={80}
            />
          </ImageListItem>
        ))}
      </ImageList>
      <div className="flex">
        <Box
          className="flex col gap-large text-text pr-huge"
          sx={{ width: { lg: "60%", sm: "50%" } }}
        >
          <div className="flex align-items-center justify-content-space-between">
            <div className="d-grid gap-large">
              <h2 className="headline-4">{discount.subtitle1}</h2>
              <h3 className="text-text body-3">{discount.subtitle2}</h3>
            </div>
            <div className="card-icon-big discount-item__discount">
              <p className="body-3 white">{discount.discount}%</p>
            </div>
          </div>
          <Divider></Divider>
          <h4 className="text-text">
            {localeStrings.discountPage.description}
          </h4>
          <div className="text-text body-1">{discount.description}</div>
          <Divider></Divider>
          <h4 className="text-text">{localeStrings.discountPage.howToUse}</h4>
          <div className="text-text body-1">{discount.application}</div>
          <div className="flex align-items-center">
            <a
              href={discount.accessUrl}
              target="_blank"
              className="text-text pointer subtitle-4 underline-link"
              rel="noreferrer"
            >
              <div className="flex gap-normal">
                {/* @ts-ignore*/}
                <OpenInNewIcon color="textColors" />
                <div className="text-text body-3">
                  {localeStrings.discountPage.moreInformation}
                </div>
              </div>
            </a>
          </div>
        </Box>
        <Box
          className="flex col align-items-center gap-large"
          sx={{ width: { lg: "40%", sm: "50%" } }}
        >
          <GetDiscountCard
            buttonText={buttonText}
            emailLink={localeStrings.discountPage.emailLink}
            remainingAmount={discount.numCouponsFromDb}
            couponsLeft={localeStrings.discountPage.couponsLeft}
            timeLeftString={timeLeftString}
            clickHandler={() => handleSendCodes().catch(() => {})}
            loading={loading}
            offerIsAvailable={localeStrings.discountPage.theOfferIsAvailable}
            canGetCodes={canGetCodes}
          />

          <HaveQuestions
            email={`mailto:${localeStrings?.errorPage?.mailToLink}`}
          />
        </Box>
      </div>
    </>
  );
}
